import * as Api from '@ViewModels';
import * as React from 'react';
import { toast } from 'sonner';
import {
	useArchiveInsurancePolicyMutation,
	useArchiveLegalMatterMutation,
	useDeleteDonationMutation,
} from '../../../../queries';
import { DeleteConfirmation } from '../../DeleteConfirmation';

const getDeleteErrorMessage = (type: DeleteType, error: Api.IOperationResultNoValue) =>
	error.systemMessage || `Failed to delete ${type}`;

export enum DeleteType {
	Policy = 'policy',
	Donation = 'donation',
	Matter = 'matter',
}

export const DeleteModal = ({
	item,
	isOpen,
	setIsOpen,
	onConfirm,
	type,
}: {
	item: Api.IDonation | Api.IInsurancePolicy | Api.ILegalMatter;
	isOpen: boolean;
	setIsOpen: React.Dispatch<React.SetStateAction<boolean>>;
	onConfirm: () => void;
	type: DeleteType;
}) => {
	const [isDeleting, setIsDeleting] = React.useState(false);

	const deleteDonationMutation = useDeleteDonationMutation({
		onSuccess: () => {
			toast.success('Donation successfully deleted');
			onConfirm();
		},
		onError: (error: Api.IOperationResultNoValue) => {
			setIsDeleting(false);
			toast.error(getDeleteErrorMessage(type, error));
		},
	});
	const archivePolicyMutation = useArchiveInsurancePolicyMutation({
		onSuccess: () => {
			toast.success('Policy successfully archived');
			onConfirm();
		},
		onError: (error: Api.IOperationResultNoValue) => {
			setIsDeleting(false);
			toast.error(getDeleteErrorMessage(type, error));
		},
	});
	const archiveMatterMutation = useArchiveLegalMatterMutation({
		onSuccess: () => {
			toast.success('Matter successfully archived');
			onConfirm();
		},
		onError: (error: Api.IOperationResultNoValue) => {
			setIsDeleting(false);
			toast.error(getDeleteErrorMessage(type, error));
		},
	});

	const confirm = async () => {
		if (type === DeleteType.Donation) {
			await deleteDonationMutation.mutateAsync({
				donationId: item.id,
			});
		} else if (type === DeleteType.Policy) {
			await archivePolicyMutation.mutateAsync({
				policyId: item.id,
			});
		} else if (type === DeleteType.Matter) {
			await archiveMatterMutation.mutateAsync({
				matterId: item.id,
			});
		}
		setIsDeleting(false);
	};

	const cancel = () => {
		setIsOpen(false);
		setIsDeleting(false);
	};

	const onFinish = (confirmDelete: boolean) => {
		setIsDeleting(true);
		if (confirmDelete) {
			confirm();
		} else {
			cancel();
		}
	};

	const message = `Are you sure you want to delete this ${type}?`;

	return <DeleteConfirmation bodyText={message} isOpen={isOpen} onFinish={onFinish} showSpinner={isDeleting} />;
};
